import React, { useState, useEffect } from 'react';
import GmailService from '../services/GmailService';

interface GmailAuthProps {
  onAuthChange: (isAuthenticated: boolean, email: string) => void;
}

export const GmailAuth: React.FC<GmailAuthProps> = ({ onAuthChange }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  
  const gmailService = GmailService.getInstance();

  useEffect(() => {
    initializeGmail();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeGmail = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const initialized = await gmailService.initialize();
      if (!initialized) {
        setError('Failed to initialize Gmail service. Please check your Google API configuration.');
        return;
      }

      const authenticated = gmailService.isSignedIn();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        const email = gmailService.getCurrentUserEmail() || 'Gmail User';
        onAuthChange(true, email);
      } else {
        onAuthChange(false, '');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initialize Gmail service');
      onAuthChange(false, '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const success = await gmailService.authenticate();
      if (success) {
        const email = gmailService.getCurrentUserEmail() || 'Gmail User';
        setIsAuthenticated(true);
        onAuthChange(true, email);
      } else {
        setError('Failed to authenticate with Gmail');
        onAuthChange(false, '');
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
      onAuthChange(false, '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await gmailService.signOut();
      setIsAuthenticated(false);
      onAuthChange(false, '');
    } catch (err: any) {
      console.error('Sign out failed:', err);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        textAlign: 'center',
        color: '#666'
      }}>
        <div style={{ marginBottom: '8px' }}>🔄</div>
        Initializing Gmail service...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '15px', 
        backgroundColor: '#f8d7da', 
        borderRadius: '8px',
        border: '1px solid #f5c6cb',
        color: '#721c24'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>⚠️ Gmail Setup Required</div>
        <div style={{ fontSize: '0.9em', marginBottom: '12px' }}>{error}</div>
        <div style={{ fontSize: '0.8em', color: '#856404' }}>
          <strong>Setup Instructions:</strong><br/>
          1. Go to Google Cloud Console<br/>
          2. APIs & Services → Credentials<br/>
          3. Edit OAuth 2.0 Client ID<br/>
          4. Add http://localhost:3000 to Authorized JavaScript origins<br/>
          5. Try the "Modern Gmail" approach (using Google Identity Services)
        </div>
        <button
          onClick={initializeGmail}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9em'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div style={{ 
        padding: '15px', 
        backgroundColor: '#d4edda', 
        borderRadius: '8px',
        border: '1px solid #c3e6cb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ color: '#155724', fontWeight: 'bold', marginBottom: '4px' }}>
            ✅ Gmail Connected
          </div>
          <div style={{ color: '#155724', fontSize: '0.9em' }}>
            Using Google Identity Services (OAuth 2.0)
          </div>
        </div>
        <button
          onClick={handleSignOut}
          style={{
            padding: '6px 12px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9em'
          }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '15px', 
      backgroundColor: '#e7f3ff', 
      borderRadius: '8px',
      border: '1px solid #b3d9ff',
      textAlign: 'center'
    }}>
      <div style={{ color: '#0066cc', marginBottom: '12px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>📧 Gmail Authentication</div>
        <div style={{ fontSize: '0.9em' }}>
          Connect using Google Identity Services<br/>
          <small>(Secure OAuth 2.0 authentication)</small>
        </div>
      </div>
      <button
        onClick={handleSignIn}
        style={{
          padding: '10px 20px',
          backgroundColor: '#4285f4',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '1em',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: '0 auto'
        }}
      >
        <span>📧</span>
        Connect Gmail
      </button>
    </div>
  );
};